/**
 * @file        src/pages/api/SupplierList.jsx
 * @description API Management → Supplier List. Registry of external travel-API
 *              suppliers (one row per supplier+module) with an Active toggle.
 *              TripJack rows carry the supplier's LIVE wallet balance; the
 *              "Balance" action still links to the Balance Sheet for the
 *              ledger view.
 * @author      Kinetika Labs
 * @created     2026-07-04
 * @changelog
 *   - 2026-07-04 Claude — Initial (modelled on the legacy admin's list).
 *   - 2026-07-30 Claude — Added a Balance column showing the live TripJack
 *                          wallet balance — the "later API" the original header
 *                          note was waiting on.
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 */
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import TableSkeleton from '../../components/TableSkeleton';
import { get, patch, apiErrorMessage } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { useWalletBalance } from '../../lib/useWalletBalance';

const MODULE_LABEL = { flight: 'Flight', hotel: 'Hotel' };
// The supplier the live balance belongs to. Anything else shows "—" until it
// has a balance API of its own.
const BALANCE_SUPPLIER = 'tj';

export default function SupplierList() {
  const queryClient = useQueryClient();
  // Same shared query as the dashboard KPI — no extra request when an admin
  // arrives here from the dashboard.
  const wallet = useWalletBalance();

  const { data, isLoading } = useQuery({
    queryKey: qk.suppliers.list(),
    queryFn: () => get('/suppliers').then((r) => r.data),
  });

  const toggleStatus = useMutation({
    mutationFn: ({ id, status }) => patch(`/suppliers/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.suppliers.list() });
      toast.ok('Supplier status updated');
    },
    onError: (e) => toast.err('Could not update status', apiErrorMessage(e, 'Try again.')),
  });

  const rows = data || [];

  return (
    <>
      <PageHead title="Supplier List" subtitle="External travel APIs · API Management" />

      <article className="panel glass">
        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>S.No</th><th>Name</th><th>Short Name</th><th>Module</th><th>Balance</th><th>Status</th><th>Index</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No suppliers configured.</td></tr>
                ) : rows.map((s, i) => (
                  <tr key={s.id}>
                    <td className="mono">{i + 1}</td>
                    <td>{s.name}</td>
                    <td className="mono">{s.short_name}</td>
                    <td>{MODULE_LABEL[s.module] || s.module}</td>
                    <td className="mono">
                      {s.short_name !== BALANCE_SUPPLIER ? '—'
                        : wallet.isLoading ? <span className="skeleton" style={{ display: 'inline-block', width: 70, height: 14 }} />
                        : wallet.display}
                    </td>
                    <td>
                      <button
                        className={`chip ${s.status ? 'ok' : 'rej'}`}
                        title={s.status ? 'Click to deactivate' : 'Click to activate'}
                        onClick={() => toggleStatus.mutate({ id: s.id, status: s.status ? 0 : 1 })}
                        disabled={toggleStatus.isPending}
                      >
                        {s.status ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="mono">{s.sort_index}</td>
                    <td><Link className="btn ghost sm" to="/account/balance">Balance</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </article>
    </>
  );
}
